import { usePage } from "@inertiajs/react"

import {
  attachOrgInboxPath,
  boardOrgWorkOrdersPath,
  confirmContractOrgRidersPath,
  confirmOrgInboxPath,
  confirmOrgWorkOrderPath,
  destroyAllOrgAccessGrantsPath,
  downloadOrgReportPath,
  editOrgCarPath,
  editOrgFacilityPath,
  newOrgCarPath,
  newOrgFacilityPath,
  orgAccessEntitlementsPath,
  orgAccessGrantPath,
  orgAccessGrantsPath,
  orgAccessInvitationsPath,
  orgAccessPath,
  orgAccessUserPath,
  orgApiTokenPath,
  orgApiTokensPath,
  orgCarPath,
  orgCarQualItemsPath,
  orgCarsPath,
  orgCloudDashboardPath,
  orgCustomersPath,
  orgFacilitiesPath,
  orgFacilityNotificationsPath,
  orgFacilityPath,
  orgFleetChatPath,
  orgFleetChatTurnPath,
  orgInboxIndexPath,
  orgLessorPath,
  orgLessorsPath,
  orgMarkNotificationsReadPath,
  orgMasterDataPath,
  orgMasterDatumPath,
  orgMemberPath,
  orgMembersPath,
  orgNotificationsPath,
  orgOrgSettingsPath,
  orgReportPath,
  orgReportSubscriptionPath,
  orgReportSubscriptionsOverviewPath,
  orgReportSubscriptionsPath,
  orgReportsPath,
  orgRequalPath,
  orgRidersPath,
  orgSavedReportRecipientPath,
  orgSavedReportRecipientsPath,
  orgSavingsPath,
  orgScorecardPath,
  orgScorecardsPath,
  orgShopsPath,
  orgWorkOrderCorrespondencesPath,
  orgWorkOrderPath,
  orgWorkOrderPodCommitmentsPath,
  orgWorkOrdersPath,
  orgWorkspacePath,
  orgWorkspacesPath,
  otmaPackageOrgWorkOrderPath,
  recomputeOrgScorecardPath,
  rejectContractOrgRidersPath,
  rejectOrgInboxPath,
  rejectOrgWorkOrderPath,
  uploadContractOrgRidersPath,
} from "@/routes"
import type { SharedProps } from "@/types"

// Matches the RequiredRouteParameter type from js-routes (not exported from the
// generated module, so we replicate it here).
type RequiredRouteParameter =
  | string
  | boolean
  | Date
  | number
  | bigint
  | { id: string | number }
  | { to_param: string | number }
  | { toParam: string | number }

/**
 * Returns org-scoped route helpers. Every page in this app lives under an org
 * slug (`/:org_slug/...`) — the pre-fork unscoped (edge) branch is gone, so
 * there is a single return shape now.
 *
 * `slug` is empty only on the org picker (`/organizations`), where no page
 * calls these helpers for navigation.
 */
export function useOrgRoutes() {
  const { organization } = usePage<SharedProps>().props
  const slug = organization?.slug ?? ""

  return {
    orgSlug: slug || null,
    // Root of an *arbitrary* org's portal (not the current one). Used by the
    // org switcher and the customers list to jump to a different org.
    orgRootPath: (targetSlug: string) => `/${targetSlug}`,
    dashboardPath: () => orgCloudDashboardPath(slug),

    // Facilities
    facilitiesPath: () => orgFacilitiesPath(slug),
    facilityPath: (facilityId: RequiredRouteParameter) =>
      orgFacilityPath(slug, facilityId),
    newFacilityPath: () => newOrgFacilityPath(slug),
    editFacilityPath: (facilityId: RequiredRouteParameter) =>
      editOrgFacilityPath(slug, facilityId),
    facilityNotificationsPath: (facilityId: RequiredRouteParameter) =>
      orgFacilityNotificationsPath(slug, facilityId),

    // Fleet. Cars are never destroyed — retirement is a status change — so
    // there is deliberately no carDestroyPath here or on the server.
    carsPath: () => orgCarsPath(slug),
    carPath: (carId: RequiredRouteParameter) => orgCarPath(slug, carId),
    newCarPath: () => newOrgCarPath(slug),
    editCarPath: (carId: RequiredRouteParameter) => editOrgCarPath(slug, carId),
    carQualItemsPath: (carId: RequiredRouteParameter) =>
      orgCarQualItemsPath(slug, carId),

    // Work orders (BadOrder / Requalification / LeaseReturn / Cleaning /
    // Restencil — one STI table, one page tree).
    workOrdersPath: () => orgWorkOrdersPath(slug),
    workOrderPath: (workOrderId: RequiredRouteParameter) =>
      orgWorkOrderPath(slug, workOrderId),
    workOrdersBoardPath: () => boardOrgWorkOrdersPath(slug),
    confirmWorkOrderPath: (workOrderId: RequiredRouteParameter) =>
      confirmOrgWorkOrderPath(slug, workOrderId),
    rejectWorkOrderPath: (workOrderId: RequiredRouteParameter) =>
      rejectOrgWorkOrderPath(slug, workOrderId),
    workOrderPodCommitmentsPath: (workOrderId: RequiredRouteParameter) =>
      orgWorkOrderPodCommitmentsPath(slug, workOrderId),
    workOrderCorrespondencesPath: (workOrderId: RequiredRouteParameter) =>
      orgWorkOrderCorrespondencesPath(slug, workOrderId),
    // FRA OTMA submission packet (PDF) for a bad order in the OTMA stage.
    otmaPackagePath: (workOrderId: RequiredRouteParameter) =>
      otmaPackageOrgWorkOrderPath(slug, workOrderId),

    // Inbox — the pending-review queue for everything the document pipeline
    // extracted but nobody has accepted yet.
    inboxPath: () => orgInboxIndexPath(slug),
    confirmInboxPath: (id: RequiredRouteParameter) =>
      confirmOrgInboxPath(slug, id),
    rejectInboxPath: (id: RequiredRouteParameter) =>
      rejectOrgInboxPath(slug, id),
    attachInboxPath: (id: RequiredRouteParameter) =>
      attachOrgInboxPath(slug, id),

    // Requal program, lease structure, repair network
    requalPath: () => orgRequalPath(slug),
    lessorsPath: () => orgLessorsPath(slug),
    lessorPath: (lessorId: RequiredRouteParameter) =>
      orgLessorPath(slug, lessorId),
    ridersPath: () => orgRidersPath(slug),
    // Lease/rider contract extraction: upload a PDF, then confirm or discard
    // the terms the pipeline read off it.
    uploadRiderContractPath: () => uploadContractOrgRidersPath(slug),
    confirmRiderContractPath: (documentId: RequiredRouteParameter) =>
      confirmContractOrgRidersPath(slug, documentId),
    rejectRiderContractPath: (documentId: RequiredRouteParameter) =>
      rejectContractOrgRidersPath(slug, documentId),
    shopsPath: () => orgShopsPath(slug),

    // Scorecards + ROI ledger
    scorecardsPath: () => orgScorecardsPath(slug),
    scorecardPath: (scorecardId: RequiredRouteParameter) =>
      orgScorecardPath(slug, scorecardId),
    recomputeScorecardPath: (scorecardId: RequiredRouteParameter) =>
      recomputeOrgScorecardPath(slug, scorecardId),
    savingsPath: () => orgSavingsPath(slug),

    // Fleet chat — GET renders the page, POST submits one turn (same URL).
    fleetChatPath: () => orgFleetChatPath(slug),
    fleetChatTurnPath: () => orgFleetChatTurnPath(slug),

    // Notifications
    notificationsPath: () => orgNotificationsPath(slug),
    markNotificationsReadPath: () => orgMarkNotificationsReadPath(slug),

    // Org settings / partner customers / master data
    orgSettingsPath: () => orgOrgSettingsPath(slug),
    customersPath: () => orgCustomersPath(slug),
    masterDataPath: () => orgMasterDataPath(slug),
    masterDatumPath: (id: RequiredRouteParameter) =>
      orgMasterDatumPath(slug, id),

    // Reports
    reportsPath: () => orgReportsPath(slug),
    reportPath: (id: RequiredRouteParameter) => orgReportPath(slug, id),
    downloadReportPath: (id: RequiredRouteParameter) =>
      downloadOrgReportPath(slug, id),
    reportSubscriptionsPath: () => orgReportSubscriptionsPath(slug),
    reportSubscriptionPath: (id: RequiredRouteParameter) =>
      orgReportSubscriptionPath(slug, id),
    reportSubscriptionsOverviewPath: () =>
      orgReportSubscriptionsOverviewPath(slug),
    savedReportRecipientsPath: () => orgSavedReportRecipientsPath(slug),
    savedReportRecipientPath: (id: RequiredRouteParameter) =>
      orgSavedReportRecipientPath(slug, id),

    // Access management
    accessPath: () => orgAccessPath(slug),
    membersPath: () => orgMembersPath(slug),
    memberPath: (id: RequiredRouteParameter) => orgMemberPath(slug, id),
    apiTokensPath: () => orgApiTokensPath(slug),
    apiTokenPath: (id: RequiredRouteParameter) => orgApiTokenPath(slug, id),
    workspacesPath: () => orgWorkspacesPath(slug),
    workspacePath: (id: RequiredRouteParameter) => orgWorkspacePath(slug, id),
    accessGrantsPath: () => orgAccessGrantsPath(slug),
    accessGrantPath: (id: RequiredRouteParameter) =>
      orgAccessGrantPath(slug, id),
    // Revoke every grant a person holds at once. The user is a query param, not
    // a path segment — this is a collection action, not a member one.
    removeAllAccessPath: (userId: RequiredRouteParameter) =>
      `${destroyAllOrgAccessGrantsPath(slug)}?user_id=${encodeURIComponent(String(userId))}`,
    accessEntitlementsPath: () => orgAccessEntitlementsPath(slug),
    accessInvitationsPath: () => orgAccessInvitationsPath(slug),
    accessUserPath: (userId: RequiredRouteParameter) =>
      orgAccessUserPath(slug, userId),

    // API paths include the org slug for tenant context
    apiFacilityPath: (facilityId: RequiredRouteParameter) =>
      `/api/v1/${slug}/facilities/${facilityId}`,
  }
}
